<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const ZONAS_HORARIAS = [
  "America/Bogota",
  "America/Mexico_City",
  "America/Lima",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/New_York"
];

const IDIOMAS = [
  { value: "es", label: "Español" },
  { value: "en", label: "Inglés" }
];

const usuarios = ref([]);
const roles = ref([]);
const supervisores = ref([]);
const permisosCatalogo = ref([]);
const cargando = ref(true);
const guardando = ref(false);

const showModal = ref(false);
const showPermisosModal = ref(false);
const isEdit = ref(false);
const usuarioPermisos = ref(null);
const permisosSeleccionados = ref([]);

const form = ref({
  id: null,
  email: "",
  password: "",
  name: "",
  lastName: "",
  roleId: "",
  supervisorId: "",
  timezone: "America/Bogota",
  language: "es",
  photoUrl: "",
  active: true
});

const headers = [
  { text: "Usuario", value: "name" },
  { text: "Correo", value: "email" },
  { text: "Rol", value: "role" },
  { text: "Reporta a", value: "supervisor_name" },
  { text: "Último acceso", value: "last_login_at" },
  { text: "Estado", value: "active" },
  { text: "Acciones", value: "actions", sortable: false }
];

const permisosPorModulo = computed(() => {
  const grupos = {};
  for (const p of permisosCatalogo.value) {
    if (!grupos[p.module]) grupos[p.module] = [];
    grupos[p.module].push(p);
  }
  return grupos;
});

const rolSeleccionadoEsAsesor = computed(() => {
  const rol = roles.value.find(r => r.id === Number(form.value.roleId));
  return rol?.name === "asesor";
});

const nombreCompleto = (u) => [u.name, u.last_name].filter(Boolean).join(" ") || "—";

const iniciales = (u) => {
  const base = [u.name, u.last_name].filter(Boolean).join(" ") || u.email;
  return base
    .split(" ")
    .map(p => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const formatFecha = (fecha) => {
  if (!fecha) return "Nunca";
  return new Date(fecha).toLocaleString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getUsuarios = async () => {
  const res = await api.get("/users");
  usuarios.value = res.data.users;
};

const getRoles = async () => {
  const res = await api.get("/roles");
  roles.value = res.data.roles;
};

const getSupervisores = async () => {
  const res = await api.get("/users/supervisors");
  supervisores.value = res.data.supervisors;
};

const getPermisosCatalogo = async () => {
  const res = await api.get("/permissions");
  permisosCatalogo.value = res.data.permissions;
};

const resetForm = () => {
  form.value = {
    id: null,
    email: "",
    password: "",
    name: "",
    lastName: "",
    roleId: "",
    supervisorId: "",
    timezone: "America/Bogota",
    language: "es",
    photoUrl: "",
    active: true
  };
};

const abrirCrear = () => {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
};

const abrirEditar = (usuario) => {
  form.value = {
    id: usuario.id,
    email: usuario.email,
    password: "",
    name: usuario.name || "",
    lastName: usuario.last_name || "",
    roleId: usuario.role_id,
    supervisorId: usuario.supervisor_id || "",
    timezone: usuario.timezone || "America/Bogota",
    language: usuario.language || "es",
    photoUrl: usuario.photo_url || "",
    active: usuario.active
  };
  isEdit.value = true;
  showModal.value = true;
};

const guardarUsuario = async () => {
  if (!form.value.email || !form.value.roleId) {
    Swal.fire({ icon: "warning", title: "Correo y rol son obligatorios" });
    return;
  }

  if (!isEdit.value && !form.value.password) {
    Swal.fire({ icon: "warning", title: "La contraseña es obligatoria para un usuario nuevo" });
    return;
  }

  guardando.value = true;

  const payload = {
    email: form.value.email,
    name: form.value.name,
    lastName: form.value.lastName,
    roleId: Number(form.value.roleId),
    supervisorId: rolSeleccionadoEsAsesor.value && form.value.supervisorId ? Number(form.value.supervisorId) : null,
    timezone: form.value.timezone,
    language: form.value.language,
    photoUrl: form.value.photoUrl || null
  };

  if (form.value.password) payload.password = form.value.password;

  try {
    if (isEdit.value) {
      payload.active = form.value.active;
      await api.put(`/users/${form.value.id}`, payload);
    } else {
      await api.post("/users", payload);
    }

    showModal.value = false;
    await getUsuarios();

    Swal.fire({
      icon: "success",
      title: isEdit.value ? "Usuario actualizado" : "Usuario creado",
      timer: 1800,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo guardar el usuario",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

const toggleEstado = async (usuario) => {
  try {
    if (usuario.active) {
      await api.delete(`/users/${usuario.id}`);
    } else {
      await api.put(`/users/${usuario.id}`, { active: true });
    }
    await getUsuarios();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cambiar el estado",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
};

const abrirPermisos = async (usuario) => {
  usuarioPermisos.value = usuario;

  try {
    const res = await api.get(`/users/${usuario.id}/permissions`);
    permisosSeleccionados.value = res.data.permissions.map(p => p.id);
    showPermisosModal.value = true;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar los permisos",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
};

const togglePermiso = (permissionId) => {
  const idx = permisosSeleccionados.value.indexOf(permissionId);
  if (idx === -1) {
    permisosSeleccionados.value.push(permissionId);
  } else {
    permisosSeleccionados.value.splice(idx, 1);
  }
};

const guardarPermisos = async () => {
  guardando.value = true;

  try {
    await api.put(`/users/${usuarioPermisos.value.id}/permissions`, {
      permissionIds: permisosSeleccionados.value
    });

    showPermisosModal.value = false;

    Swal.fire({
      icon: "success",
      title: "Permisos actualizados",
      timer: 1500,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron guardar los permisos",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
};

onMounted(async () => {
  cargando.value = true;
  try {
    await Promise.all([getUsuarios(), getRoles(), getSupervisores(), getPermisosCatalogo()]);
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la gestión de usuarios",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 users-header-title">
        <i class="bi bi-people-fill me-2"></i>
        Gestión de Usuarios
      </h4>

      <argon-button color="success" @click="abrirCrear">
        <i class="bi bi-plus-circle me-1"></i>Nuevo usuario
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <DataTable :headers="headers" :items="usuarios" :rows-per-page="20">
          <template #item-name="item">
            <div class="d-flex align-items-center gap-2">
              <img v-if="item.photo_url" :src="item.photo_url" class="avatar-sm rounded-circle" alt="">
              <div v-else class="avatar-sm rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center">
                {{ iniciales(item) }}
              </div>
              <span>{{ nombreCompleto(item) }}</span>
            </div>
          </template>

          <template #item-role="{ role }">
            <span
              class="badge"
              :class="{
                'bg-gradient-dark': role === 'admin' || role === 'platform_admin',
                'bg-gradient-info': role === 'supervisor',
                'bg-gradient-secondary': role === 'asesor'
              }"
            >
              {{ role === 'admin' ? 'Administrador' : role === 'supervisor' ? 'Supervisor' : role === 'platform_admin' ? 'Plataforma' : 'Asesor' }}
            </span>
          </template>

          <template #item-supervisor_name="{ supervisor_name }">
            {{ supervisor_name || "—" }}
          </template>

          <template #item-last_login_at="{ last_login_at }">
            {{ formatFecha(last_login_at) }}
          </template>

          <template #item-active="{ active }">
            <span class="badge" :class="active ? 'bg-success' : 'bg-gradient-secondary'">
              {{ active ? "Activo" : "Inactivo" }}
            </span>
          </template>

          <template #item-actions="item">
            <div class="d-flex gap-1">
              <button class="btn btn-sm btn-light" title="Editar" @click="abrirEditar(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-sm btn-outline-primary" title="Permisos" @click="abrirPermisos(item)">
                <i class="bi bi-shield-lock"></i>
              </button>
              <button
                class="btn btn-sm"
                :class="item.active ? 'btn-outline-danger' : 'btn-outline-success'"
                :title="item.active ? 'Desactivar' : 'Activar'"
                @click="toggleEstado(item)"
              >
                <i class="bi" :class="item.active ? 'bi-slash-circle' : 'bi-check-circle'"></i>
              </button>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR USUARIO -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">

          <div class="modal-header">
            <h5>{{ isEdit ? "Editar usuario" : "Nuevo usuario" }}</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small">Nombre</label>
                <argon-input v-model="form.name" placeholder="Mateo" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">Apellido</label>
                <argon-input v-model="form.lastName" placeholder="Mena" />
              </div>
            </div>

            <div class="row g-3 mt-3">
              <div class="col-md-6">
                <label class="form-label small">Correo</label>
                <argon-input v-model="form.email" type="email" placeholder="correo@empresa.com" />
              </div>
              <div class="col-md-6">
                <label class="form-label small">
                  Contraseña
                  <span v-if="isEdit" class="text-muted">(dejar en blanco para no cambiarla)</span>
                </label>
                <argon-input v-model="form.password" type="password" placeholder="••••••••" />
              </div>
            </div>

            <div class="row g-3 mt-3">
              <div class="col-md-6">
                <label class="form-label small">Rol</label>
                <select v-model="form.roleId" class="form-select">
                  <option value="" disabled>Selecciona...</option>
                  <option v-for="rol in roles" :key="rol.id" :value="rol.id">
                    {{ rol.name === 'admin' ? 'Administrador' : rol.name === 'supervisor' ? 'Supervisor' : 'Asesor' }}
                  </option>
                </select>
              </div>

              <div v-if="rolSeleccionadoEsAsesor" class="col-md-6">
                <label class="form-label small">Reporta a (supervisor)</label>
                <select v-model="form.supervisorId" class="form-select">
                  <option value="">Sin supervisor</option>
                  <option v-for="sup in supervisores" :key="sup.id" :value="sup.id">
                    {{ [sup.name, sup.last_name].filter(Boolean).join(" ") }}
                  </option>
                </select>
              </div>

                     <div class="col-md-6">
                <label class="form-label small">Zona horaria</label>
                <select v-model="form.timezone" class="form-select">
                  <option v-for="tz in ZONAS_HORARIAS" :key="tz" :value="tz">{{ tz }}</option>
                </select>
              </div>
            </div>

            <div class="row g-3 mt-3">
       

              <div class="col-md-6">
                <label class="form-label small">Idioma</label>
                <select v-model="form.language" class="form-select">
                  <option v-for="idioma in IDIOMAS" :key="idioma.value" :value="idioma.value">{{ idioma.label }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label small">Foto (URL)</label>
                <argon-input v-model="form.photoUrl" placeholder="https://..." />
              </div>
            </div>

            <div v-if="isEdit" class="row g-3 mt-3">
              <div class="col-12">
                <div class="form-check form-switch">
                  <input id="estadoUsuario" v-model="form.active" class="form-check-input" type="checkbox" role="switch">
                  <label class="form-check-label" for="estadoUsuario">Usuario activo</label>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary mb-0" @click="showModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarUsuario">
              Guardar
            </argon-button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- MODAL PERMISOS -->
    <div v-if="showPermisosModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-shield-lock me-1"></i>Permisos de {{ nombreCompleto(usuarioPermisos) }}</h5>
            <button class="btn-close" @click="showPermisosModal = false"></button>
          </div>

          <div class="modal-body">
            <p class="text-muted small">
              Administradores y usuarios de plataforma ya tienen acceso total; estos permisos aplican a
              supervisores y asesores.
            </p>

            <div v-for="(permisos, modulo) in permisosPorModulo" :key="modulo" class="mb-3">
              <h6 class="text-uppercase small text-muted">{{ modulo }}</h6>
              <div v-for="permiso in permisos" :key="permiso.id" class="form-check">
                <input
                  :id="`permiso-${permiso.id}`"
                  class="form-check-input"
                  type="checkbox"
                  :checked="permisosSeleccionados.includes(permiso.id)"
                  @change="togglePermiso(permiso.id)"
                >
                <label class="form-check-label" :for="`permiso-${permiso.id}`">
                  {{ permiso.description }}
                </label>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary mb-0" @click="showPermisosModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarPermisos">
              Guardar permisos
            </argon-button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showPermisosModal" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.avatar-sm {
  width: 32px;
  height: 32px;
  min-width: 32px;
  font-size: 0.75rem;
  object-fit: cover;
}

.users-header-title {
  color: #fff !important;
}
</style>
